import { useCoreConfig, getCurrentInstance, reactiveComputed, inject, toValue, provide, computed } from '#imports'
import type { InjectionKey, MaybeRefOrGetter, Ref } from '#imports'
import { createDefu } from 'defu'
import type { Defaults } from '@core/types'
import type { PartialDeep, SetRequired } from 'type-fest'
import { lowerFirst, pickBy, upperFirst } from 'es-toolkit'

const defu = createDefu((obj, key, value) => {
    if (Array.isArray(obj[key]) || Array.isArray(value)) {
        obj[key] = value
        return true
    }
})

export const DefaultsKey = Symbol() as InjectionKey<Ref<Defaults>>

export interface UseDefaultsOptions<Props extends Record<string, any>, LocalDefaults extends PartialDeep<Props>> {
    props: MaybeRefOrGetter<Props>
    defaults?: LocalDefaults
    /**
     * The components name.
     *
     * Not required since it can be resolved from the current instance.
     */
    name?: string
}

function getSubComponents(config: Record<string, any>) {
    return pickBy(config, (_, key) => upperFirst(key as unknown as string) === key)
}

function getProps(config: Record<string, any>) {
    return pickBy(config, (_, key) => lowerFirst(key as unknown as string) === key)
}

export const useDefaults = <Props extends Record<string, any>, LocalDefaults extends PartialDeep<Props>>(options: UseDefaultsOptions<Props, LocalDefaults>) => {
    const { props, defaults: localDefaults } = options

    const { defaults: globalDefaults } = useCoreConfig()
    const parentDefaults = inject(DefaultsKey, undefined)

    let name: string | undefined = options.name

    // Try resolving name from component
    if (!name) {
        const component = getCurrentInstance()

        if (!component) {
            throw new Error('useDefaults must be used inside a Vue component.')
        }
        name = component.type.__name
    }

    if (!name) {
        throw new Error('Name could not be found.')
    }

    const provideDefaults = computed(() => {
        const layers = new Set<any>()

        if (parentDefaults?.value?.[name as keyof Defaults]) {
            layers.add(
                getSubComponents(parentDefaults.value[name as keyof Defaults]!)
            )
        }
        if (parentDefaults) layers.add(parentDefaults.value)

        // Only add global defaults, if there is no parent. If there is a parent, globals will be part of the parents'
        if (!parentDefaults) {
            if (globalDefaults?.[name as keyof Defaults]) {
                layers.add(
                    getSubComponents(globalDefaults[name as keyof Defaults]!)
                )
            }
            if (globalDefaults) layers.add({
                ...globalDefaults
            })
        }

        const propsRaw = toValue(props) || {}
        // Get global options from subcomponent props. It will find all props that start with a capital letter, which are subcomponent props e.g.
        /**
         * {
         * MyComponent: {
         *  MyComponent2: {
         *   color: 'primary'}
         *  }
         * }
         */
        const initial = getSubComponents(propsRaw)

        const combined = defu(
            initial,
            ...Array.from(layers.values())
        )

        return combined as Defaults
    })

    const propsWithDefaults = reactiveComputed(() => {
        const propsRaw = toValue(props)
        // Get all props that are for _this_ component and not subcomponent props.
        const initial = getProps(propsRaw)

        const layers = []

        layers.push(provideDefaults.value?.[name as keyof Defaults])

        if (localDefaults) {
            layers.push(localDefaults)
        }

        const combined = defu(initial, ...layers) as unknown as LocalDefaults extends undefined ? Props : SetRequired<Props, Extract<keyof LocalDefaults, keyof Props>>

        return combined
    })

    provide(DefaultsKey, provideDefaults)

    return propsWithDefaults
}
